import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Download, FileUp, History, ShieldCheck, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatTimestamp } from "@/utils/datetime";
import { newestFirst } from "@/utils/sortHistory";


const API = `${process.env.REACT_APP_BACKEND_URL}/api/project-transfer`;


export function ProjectTransferPanel({ canTransfer, projectId }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [review, setReview] = useState(null);
  const [isWorking, setIsWorking] = useState(false);
  const [history, setHistory] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);

  const loadHistory = useCallback(async () => {
    setIsHistoryLoading(true);
    try {
      const response = await axios.get(`${API}/projects/${projectId}/import-history`);
      setHistory(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import history could not be loaded.");
    } finally {
      setIsHistoryLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (open) loadHistory();
  }, [loadHistory, open]);

  if (!canTransfer) return null;

  const download = async (format) => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/export`, {
        params: { format },
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `project-${projectId}.${format === "xlsx" ? "xlsx" : "zip"}`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Export could not be generated.");
    }
  };

  const validateImport = async (event) => {
    event.preventDefault();
    if (!file) return;
    setIsWorking(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const validated = await axios.post(
        `${API}/projects/${projectId}/import/validate`,
        formData,
      );
      const reviewed = await axios.get(
        `${API}/projects/${projectId}/import/${validated.data.import_id}`,
      );
      setReview(reviewed.data);
      toast.success("Import staged for review.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import could not be validated.");
    } finally {
      setIsWorking(false);
    }
  };

  const approveImport = async () => {
    const validStageIds = review.stages.filter((stage) => !stage.errors.length).map((stage) => stage.id);
    if (!validStageIds.length) return;
    setIsWorking(true);
    try {
      await axios.post(`${API}/projects/${projectId}/import/${review.import_id}/approve`, {
        stage_ids: validStageIds,
      });
      toast.success("Reviewed project updates applied.");
      setReview(null);
      setFile(null);
      await loadHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Reviewed import could not be approved.");
    } finally {
      setIsWorking(false);
    }
  };

  const validCount = review?.stages.filter((stage) => !stage.errors.length).length || 0;
  const errorCount = review?.stages.filter((stage) => stage.errors.length).length || 0;
  const lifecycleLocked = history.some((entry) => entry.lifecycle_applied);

  return (
    <>
      <button
        className="table-edit-button"
        data-testid="project-transfer-open-button"
        onClick={() => setOpen(true)}
        style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem" }}
        type="button"
      >
        <ArrowLeftRight size={14} /> Transfer & Imports
      </button>
      <Dialog onOpenChange={setOpen} open={open}>
        <DialogContent
          data-testid="project-transfer-dialog"
          style={{ maxWidth: 900, maxHeight: "90vh", overflowY: "auto" }}
        >
          <DialogHeader>
            <DialogTitle>Project transfer & import history</DialogTitle>
          </DialogHeader>
          <section className="project-transfer-panel" data-testid="project-transfer-panel">
      <div>
        <span className="operations-kicker">Project transfer</span>
        <h2>Export and reviewed import</h2>
        <p>
          Export this project’s records, edit only the exported template, then validate every
          proposed update before approval.
        </p>
      </div>
      <div className="project-transfer-actions">
        <button data-testid="project-export-xlsx-button" onClick={() => download("xlsx")} type="button">
          <Download size={15} /> Export Excel
        </button>
        <button data-testid="project-export-csv-button" onClick={() => download("csv")} type="button">
          <Download size={15} /> Export CSV
        </button>
      </div>
      <form data-testid="project-import-form" onSubmit={validateImport}>
        <input
          accept=".xlsx,.zip"
          data-testid="project-import-file-input"
          onChange={(event) => setFile(event.target.files?.[0] || null)}
          required
          type="file"
        />
        <button data-testid="project-import-validate-button" disabled={!file || isWorking} type="submit">
          <FileUp size={15} /> Validate and stage
        </button>
      </form>
      {review && (
        <div className="project-transfer-review" data-testid="project-import-review">
          <strong><ShieldCheck size={15} /> {validCount} reviewed updates ready</strong>
          <span>{errorCount} rows need correction before a new import can be staged.</span>
          <ul>
            {review.stages.slice(0, 12).map((stage) => (
              <li data-testid={`project-import-stage-${stage.id}`} key={stage.id}>
                {stage.entity} row {stage.row_number}: {stage.errors.length ? stage.errors.join(", ") : stage.changed_fields.join(", ") || "no changes"}
              </li>
            ))}
          </ul>
          <button
            data-testid="project-import-approve-button"
            disabled={!validCount || isWorking || errorCount > 0}
            onClick={approveImport}
            type="button"
          >
            Approve reviewed updates
          </button>
        </div>
      )}
      <section className="project-transfer-history" data-testid="project-import-history">
        <div className="project-transfer-history-heading">
          <div>
            <span className="operations-kicker">Import History</span>
            <h3>Approved imports</h3>
          </div>
          <button
            data-testid="project-import-history-refresh-button"
            disabled={isHistoryLoading}
            onClick={loadHistory}
            type="button"
          >
            <History size={15} /> Refresh history
          </button>
        </div>
        <p>
          Original files are processed transiently and are not retained. This audit history remains
          after import approval.
        </p>
        <div
          className={`project-import-lifecycle-lock ${lifecycleLocked ? "is-locked" : ""}`}
          data-testid="project-import-lifecycle-lock"
        >
          <strong>{lifecycleLocked ? "Lifecycle import locked" : "Lifecycle import available"}</strong>
          <span>
            {lifecycleLocked
              ? "A prior approved import set lifecycle/status. Future imports cannot override it."
              : "The first approved import with lifecycle/status changes may set them once."}
          </span>
        </div>
        {isHistoryLoading && <p data-testid="project-import-history-loading">Loading history…</p>}
        {!isHistoryLoading && !history.length && (
          <p data-testid="project-import-history-empty">No approved imports recorded yet.</p>
        )}
        <ol>
          {newestFirst(history, "approved_at").map((entry) => (
            <li data-testid={`project-import-history-${entry.id}`} key={entry.id}>
              <strong>{entry.source_filename}</strong>
              <span>{formatTimestamp(entry.approved_at)} · {entry.rows} reviewed rows</span>
              <small>
                {entry.lifecycle_applied
                  ? "Lifecycle/status applied and now locked for later imports."
                  : "Lifecycle/status remains under normal app workflow."}
              </small>
              <em>
                Changed fields: {entry.changed_fields.length ? entry.changed_fields.join(", ") : "none"}
              </em>
            </li>
          ))}
        </ol>
      </section>
    </section>
        </DialogContent>
      </Dialog>
    </>
  );
}